import AdResultsView from "@/components/ad-results-view";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAvailableBrands,
  useGetAdsByPageId,
  type AdLibraryResponse,
  type Brand,
} from "@/hooks/api/use-brand-ads";
import {
  useAddCompetitor,
  useProject,
  useRemoveCompetitor,
} from "@/hooks/api/use-projects";
import { cn } from "@/lib/utils";
import { Plus, Settings, Target, Trash, TrendingUp, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

export default function ProjectDetailPage() {
  const { id, competitorId } = useParams<{
    id: string;
    competitorId: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const projectId = id ? parseInt(id) : 0;
  const { data: project, isLoading: isLoadingProject } = useProject(id!);
  const { data: availableBrands } = useAvailableBrands();
  const addCompetitor = useAddCompetitor(projectId);
  const [open, setOpen] = useState(false);
  const [brandId, setBrandId] = useState<string>();
  const [selectedCompetitor, setSelectedCompetitor] = useState<Brand | null>(
    null
  );
  const { data: competitorAds, isLoading: isLoadingAds } = useGetAdsByPageId(
    selectedCompetitor?.facebook_page_id ||
      project?.brand?.facebook_page_id ||
      null
  );
  const [competitorToRemove, setCompetitorToRemove] = useState<number | null>(
    null
  );
  const removeCompetitorMutation = useRemoveCompetitor(
    projectId,
    competitorToRemove || 0
  );

  useEffect(() => {
    // Reset selection if no competitorId in URL
    if (!competitorId) {
      setSelectedCompetitor(null);
      return;
    }

    // Wait for project data to load
    if (isLoadingProject || !project?.competitors) {
      return;
    }

    // Find and set the selected competitor
    const competitor = project.competitors.find(
      (c) => c.id === parseInt(competitorId)
    );
    setSelectedCompetitor(competitor || null);
  }, [competitorId, project, isLoadingProject, location]);

  const handleAddCompetitor = async () => {
    if (!brandId) return;

    try {
      await addCompetitor.mutateAsync({
        brand_id: parseInt(brandId),
      });
      toast.success("Competitor added successfully");
      setOpen(false);
      setBrandId(undefined);
    } catch (error) {
      toast.error("Failed to add competitor");
    }
  };

  const handleRemoveCompetitor = async (brandId: number) => {
    try {
      setCompetitorToRemove(brandId);
      await removeCompetitorMutation.mutateAsync();
      toast.success("Competitor removed successfully");
      if (selectedCompetitor?.id === brandId) {
        setSelectedCompetitor(null);
      }
    } catch (error) {
      toast.error("Failed to remove competitor");
    } finally {
      setCompetitorToRemove(null);
    }
  };

  // Target Brand Skeleton component
  const TargetBrandSkeleton = () => (
    <div className="flex items-center">
      <Skeleton className="w-12 h-12 rounded-full flex-shrink-0" />
      <div className="ml-4 space-y-2">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
  );

  // Determine if we should show the ad results section
  const shouldShowAdResults =
    !isLoadingProject &&
    ((!selectedCompetitor && project?.brand) || selectedCompetitor);

  // Determine if we should show the ad results loading state
  const shouldShowAdResultsLoading =
    !isLoadingProject &&
    isLoadingAds &&
    ((!selectedCompetitor && project?.brand) || selectedCompetitor);

  // Empty ad library response for loading state
  const emptyAdLibraryResponse: AdLibraryResponse = {
    results: [],
    overview: "",
    created_at: "",
    strategy_evolution: "",
    status: "success",
  };

  // Update the competitor selection handler
  const handleCompetitorSelection = (brand: Brand | null) => {
    setSelectedCompetitor(brand);
    if (brand) {
      navigate(`/projects/${id}/competitors/${brand.id}`);
    } else {
      navigate(`/projects/${id}`);
    }
  };

  return (
    <div>
      <div className="space-y-6">
        {/* Brand Header Section */}
        <Card
          className="bg-background transition-all hover:shadow-md p-4 mb-6 cursor-pointer"
          onClick={() => !isLoadingProject && handleCompetitorSelection(null)}
        >
          <div className="flex justify-between items-center">
            {isLoadingProject ? (
              <TargetBrandSkeleton />
            ) : (
              project?.brand && (
                <div className="flex items-center">
                  <div className="relative w-12 h-12 rounded-full overflow-hidden bg-neutral-800 flex-shrink-0 cursor-pointer">
                    <img
                      src={project.brand.profile_photo}
                      alt={project.brand.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <h2 className="text-xl font-bold">
                        {project.brand.name}
                      </h2>
                    </div>
                    {project.brand.about && (
                      <p className="text-sm text-neutral-400 mt-1 line-clamp-1 max-w-3xl">
                        {project.brand.industry}
                      </p>
                    )}
                  </div>
                </div>
              )
            )}

            <div className="flex items-center gap-2">
              <Link
                to={`/projects/${id}/settings`}
                className="inline-flex items-center justify-center rounded-md w-9 h-9 hover:bg-accent hover:text-accent-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                <Settings className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </Card>

        {/* Competitors Section */}
        <Card className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Competitors</h2>
            <Dialog open={open} onOpenChange={setOpen}>
              {!isLoadingProject && project?.competitors && (
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Competitor
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Competitor</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Select value={brandId} onValueChange={setBrandId} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a competitor" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableBrands
                          ?.filter(
                            (brand) =>
                              brand.id !== project?.brand.id &&
                              !project?.competitors?.some(
                                (competitor) => competitor.id === brand.id
                              )
                          )
                          .map((brand) => (
                            <SelectItem
                              key={brand.id}
                              value={brand.id.toString()}
                            >
                              {brand.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleAddCompetitor} className="w-full">
                    Add
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="relative">
            {isLoadingProject ? (
              <div className="overflow-x-auto pb-2">
                <div className="flex gap-4 pb-2">
                  {Array(3)
                    .fill(0)
                    .map((_, i) => (
                      <div key={i} className="flex-shrink-0 min-w-[150px]">
                        <Card className="transition-all p-3 hover:shadow-md">
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
                            <div className="flex-1 space-y-2">
                              <Skeleton className="h-4 w-2/3" />
                              <Skeleton className="h-3 w-1/2" />
                            </div>
                            <Skeleton className="h-8 w-8 rounded-md flex-shrink-0" />
                          </div>
                        </Card>
                      </div>
                    ))}
                </div>
              </div>
            ) : project?.competitors && project.competitors.length > 0 ? (
              <div className="overflow-x-auto pb-2">
                <div className="flex gap-4 pb-2">
                  {project.competitors.map((brand) => (
                    <div key={brand.id} className="flex-shrink-0 min-w-[150px]">
                      <Card
                        className={cn(
                          "transition-all p-3 hover:shadow-md cursor-pointer",
                          selectedCompetitor?.id === brand.id &&
                            "bg-primary/5 border-primary/20"
                        )}
                        onClick={() => handleCompetitorSelection(brand)}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              "h-10 w-10 rounded-full overflow-hidden",
                              selectedCompetitor?.id === brand.id &&
                                "ring-2 ring-primary/20 ring-offset-2"
                            )}
                          >
                            <img
                              src={brand.profile_photo}
                              alt={brand.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center">
                              <h3
                                className={cn(
                                  "font-semibold truncate text-sm",
                                  selectedCompetitor?.id === brand.id &&
                                    "text-primary"
                                )}
                              >
                                {brand.name}
                              </h3>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {brand.industry}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveCompetitor(brand.id);
                            }}
                            className="shrink-0"
                            disabled={
                              isLoadingAds &&
                              selectedCompetitor?.id === brand.id
                            }
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-8">
                <EmptyState
                  title="No Competitors Added"
                  description="Add competitors to track and analyze their ad strategies."
                  icons={[Target, Users, TrendingUp]}
                  action={{
                    label: "Add First Competitor",
                    onClick: () => setOpen(true),
                  }}
                  className="min-h-0"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Ad Results Section - Show loading state or actual content */}
        {shouldShowAdResultsLoading ? (
          <AdResultsView
            brand={selectedCompetitor || project!.brand}
            competitorAds={emptyAdLibraryResponse}
            isLoading={true}
          />
        ) : (
          shouldShowAdResults &&
          competitorAds && (
            <AdResultsView
              brand={selectedCompetitor || project!.brand}
              competitorAds={competitorAds}
              onClose={
                selectedCompetitor
                  ? () => setSelectedCompetitor(null)
                  : undefined
              }
            />
          )
        )}
      </div>
    </div>
  );
}
